/**
 * Screen 3 — Home. Featured programme, what is coming up, notices.
 *
 * The featured programme is simply the next one. A hand-picked "featured"
 * flag is a field a volunteer forgets to clear, and a stale hero on the home
 * screen is worse than no hero.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { LocalisedField } from "../components/Localised";
import { ErrorNote, Note, Shell } from "../components/Shell";
import type { Locale } from "../i18n";
import { api, isSignedIn, OfflineError, type ProgramSummary, type Me } from "../lib/api";
import { ProgramArt } from "../components/ProgramArt";
import { formatDateTime, formatIdr } from "../lib/format";

export default function Home({ locale }: { locale: Locale }) {
  const { t } = useTranslation();
  const [programs, setPrograms] = useState<ProgramSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [me, setMe] = useState<Me | null>(null);
  const [ticketFor, setTicketFor] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    api
      .programs({}, locale)
      .then((body) => setPrograms(body.programs))
      .catch((err) =>
        setError(err instanceof OfflineError ? t("error.network") : t("error.generic")),
      );
    if (isSignedIn()) {
      api.me().then(setMe).catch(() => undefined);
      api
        .myBookings(locale)
        .then((b) =>
          setTicketFor(
            new Map(
              b.upcoming
                .filter((x) => x.state === "PAID" || x.state === "CHECKED_IN")
                .map((x) => [x.program.slug, x.ref]),
            ),
          ),
        )
        .catch(() => undefined);
    }
  }, [locale, t]);

  const [featured, ...rest] = programs ?? [];

  return (
    <Shell masthead area="home" title={t("app.name")}>
      {/* A name when we know it. "Hello!" to someone signed in reads like a
          form letter. First name only -- that is how the hall greets people. */}
      <p className="greeting">
        {me ? t("home.greeting_named", { name: me.name.split(" ")[0] }) : t("home.greeting")}
      </p>

      {error && <ErrorNote>{error}</ErrorNote>}
      {programs === null && !error && <Note>{t("common.loading")}</Note>}
      {programs?.length === 0 && <Note>{t("programs.none")}</Note>}

      {featured && (
        <section data-area="programs" className="hero">
          <ProgramArt
            poster={featured.poster_url}
            category={featured.category}
            labelKey={featured.category_label_key}
          />
          <h2 className="section">{t("home.featured")}</h2>
          <LocalisedField content={featured.text} field="name" as="h3" className="hero__title" note />
          <p className="hero__when">{formatDateTime(featured.date_time, locale)}</p>
          <p>{featured.venue}</p>
          {featured.from_price_idr !== null && (
            <p className="money">
              {t("programs.from_price", { price: formatIdr(featured.from_price_idr) })}
            </p>
          )}
          {/* Already holding a ticket? Then the useful action is seeing it,
              not being invited to buy another. */}
          {ticketFor.has(featured.slug) ? (
            <Link className="btn btn--primary" to={`/tickets/${ticketFor.get(featured.slug)}`}>
              {t("ticket.title")}
            </Link>
          ) : featured.sold_out ? (
            <span className="badge badge--muted">{t("programs.sold_out")}</span>
          ) : (
            <Link className="btn btn--primary" to={`/programs/${featured.slug}`}>
              {t("programs.book_now")}
            </Link>
          )}
        </section>
      )}

      {rest.length > 0 && (
        <>
          <h2 className="section">{t("home.upcoming")}</h2>
          <ul className="cards">
            {rest.slice(0, 5).map((p) => (
              <li key={p.id} className={`card${p.sold_out ? " card--sold-out" : ""}`}>
                <LocalisedField content={p.text} field="name" as="h3" className="card__title" note />
                <p className="card__meta card__when">{formatDateTime(p.date_time, locale)}</p>
                <Link className="btn" to={`/programs/${p.slug}`}>
                  {t("programs.view_details")}
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}

      {/* One honest link to the full menu, not three section names strung
          together with dots. */}
      <Link className="btn" to="/menu">
        {t("nav.more")}
      </Link>
    </Shell>
  );
}
